import React, { useRef, useEffect, useMemo } from 'react'
import ApexCharts from 'apexcharts'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'

export default function ChannelBreakdownChart({ data = [], title, height = 300, loading }) {
  /* data shape: [{ label: 'WhatsApp', value: 3200, color: '#25D366' }] */

  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const options = useMemo(() => ({
    chart: { type: 'donut', height, fontFamily: "'Inter', sans-serif" },
    labels: data.map(d => d.label),
    colors: data.map(d => d.color),
    legend: {
      position: 'bottom',
      fontSize: '12px',
      fontWeight: 600,
      markers: { radius: 3 },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '11px', fontWeight: 700 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '58%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '13px',
              fontWeight: 700,
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString('en-IN'),
            },
          },
        },
      },
    },
    stroke: { width: 2, colors: ['#fff'] },
    tooltip: { y: { formatter: (v) => v?.toLocaleString('en-IN') } },
  }), [data, height])

  const chartSeries = useMemo(() => data.map(d => d.value), [data])

  useEffect(() => {
    if (!chartRef.current) return
    if (chartInstance.current) chartInstance.current.destroy()
    chartInstance.current = new ApexCharts(chartRef.current, { ...options, series: chartSeries })
    chartInstance.current.render()
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null } }
  }, [options, chartSeries])

  if (loading) {
    return (
      <Paper sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Skeleton width={140} height={20} sx={{ mb: 2 }} />
        <Skeleton variant='circular' width={180} height={180} />
      </Paper>
    )
  }

  if (!data.length) return null

  return (
    <Paper sx={{ p: 2.5 }}>
      {title && <Typography variant='subtitle1' sx={{ mb: 1 }}>{title}</Typography>}
      <div ref={chartRef} />
    </Paper>
  )
}
